/**
 * API Client with Microsoft Entra ID + Google OAuth Authentication
 * All requests include OAuth bearer token
 * HIPAA: TLS 1.2+ enforced by Azure App Service
 */

import axios, { AxiosInstance } from 'axios'
import { IPublicClientApplication } from '@azure/msal-browser'
import { apiRequest, AuthProvider } from '../config/authConfig'

const API_BASE_URL = import.meta.env.VITE_BACKEND_URL || 'https://appbemvphyyske2w3xwvm.azurewebsites.net'

// Create axios instance
const api: AxiosInstance = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
  timeout: parseInt(import.meta.env.VITE_API_TIMEOUT || '60000'),  // Default: 60 seconds (override per-request for AI endpoints)
})

// Authentication state
let msalInstance: IPublicClientApplication | null = null
let googleCredential: string | null = null
let currentAuthProvider: AuthProvider | null = null
let is401InProgress = false
const AUTH_REDIRECT_DEBOUNCE_MS = 3000

export const initializeAuth = (instance: IPublicClientApplication) => {
  msalInstance = instance
  currentAuthProvider = 'microsoft'
}

export const setGoogleCredential = (credential: string) => {
  googleCredential = credential
  currentAuthProvider = 'google'
  // HIPAA: Use sessionStorage (clears on browser close) instead of localStorage
  // Prevents credential theft via XSS attacks on persistent storage
  sessionStorage.setItem('google_credential', credential)
  sessionStorage.setItem('auth_provider', 'google')
}

export const clearGoogleCredential = () => {
  googleCredential = null
  // HIPAA: Clear from sessionStorage
  sessionStorage.removeItem('google_credential')
  sessionStorage.removeItem('auth_provider')
  if (!msalInstance || msalInstance.getAllAccounts().length === 0) {
    currentAuthProvider = null
  }
}

// HIPAA: Restore Google credential from sessionStorage on page load
// Note: sessionStorage does not persist across browser restarts (by design for security)
export const restoreGoogleCredential = (): boolean => {
  const stored = sessionStorage.getItem('google_credential')
  const provider = sessionStorage.getItem('auth_provider')
  if (stored && provider === 'google') {
    googleCredential = stored
    currentAuthProvider = 'google'
    return true
  }
  return false
}

export const getCurrentAuthProvider = (): AuthProvider | null => {
  return currentAuthProvider
}

/**
 * Get current auth token (reusable across interceptors and manual requests)
 * @returns Bearer token or empty string if not authenticated
 */
export const getAuthToken = async (): Promise<string> => {
    // Try Google credential first if available
    if (googleCredential) {
    return `Bearer ${googleCredential}`
    }
    
    // Fall back to Microsoft token
    if (msalInstance) {
      try {
        const accounts = msalInstance.getAllAccounts()
        if (accounts.length > 0) {
          const response = await msalInstance.acquireTokenSilent({
            ...apiRequest,
            account: accounts[0],
          })
        return `Bearer ${response.accessToken}`
        }
      } catch (error) {
        console.error('Failed to acquire Microsoft token:', error)
      }
  }
  
  return ''
}

// Request interceptor - Add auth token to every request
api.interceptors.request.use(
  async (config) => {
    const token = await getAuthToken()
    if (token) {
      config.headers.Authorization = token
    }
    return config
  },
  (error) => Promise.reject(error)
)

// Response interceptor - Handle auth errors with token refresh
api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config

    if (error.response?.status === 401 && !originalRequest._retry) {
      // Prevent concurrent 401 handling (multiple requests can 401 simultaneously)
      if (is401InProgress) {
        return Promise.reject(error)
      }
      is401InProgress = true
      originalRequest._retry = true

      // For Google auth - no silent refresh available, must re-login
      if (googleCredential) {
        console.error('Google token expired or invalid')
        clearGoogleCredential()
        window.location.href = '/'
        setTimeout(() => { is401InProgress = false }, AUTH_REDIRECT_DEBOUNCE_MS)
        return Promise.reject(error)
      }

      // For Microsoft auth - attempt silent token refresh before forcing re-login
      if (msalInstance) {
        try {
          const accounts = msalInstance.getAllAccounts()
          if (accounts.length > 0) {
            // Attempt silent token refresh
            const response = await msalInstance.acquireTokenSilent({
              ...apiRequest,
              account: accounts[0],
              forceRefresh: true,
            })

            // Retry original request with new token
            originalRequest.headers.Authorization = `Bearer ${response.accessToken}`
            is401InProgress = false
            return api(originalRequest)
          }
        } catch (refreshError) {
          console.error('[API] Token refresh failed:', refreshError)
        }

        // Refresh failed - clear cache and redirect to login
        try {
          await msalInstance.clearCache()
        } catch (e) {
          console.log('[API] Failed to clear MSAL cache:', e)
        }
      }

      // Redirect to login page
      window.location.href = '/'
      setTimeout(() => { is401InProgress = false }, AUTH_REDIRECT_DEBOUNCE_MS)
    }
    return Promise.reject(error)
  }
)

export default api

// ============================================================================
// API Helper Functions
// ============================================================================

/**
 * Project Files API
 * HIPAA: Files stored in Azure Blob Storage with encryption at rest
 */

/**
 * List all files attached to a project
 * GET /api/projects/{project_id}/files
 */
export async function listProjectFiles(projectId: string): Promise<any[]> {
  const response = await api.get(`/api/projects/${projectId}/files`)
  return response.data || []
}

/**
 * Upload a file to a project
 * POST /api/projects/{project_id}/files
 * @param projectId - Project ID
 * @param file - File to upload (max 10MB)
 * @returns ProjectFile response
 */
export async function uploadProjectFile(projectId: string, file: File): Promise<any> {
  const formData = new FormData()
  formData.append('file', file)

  const response = await api.post(`/api/projects/${projectId}/files`, formData, {
    headers: {
      'Content-Type': 'multipart/form-data'
    }
  })

  return response.data
}

/**
 * Delete a file from a project
 * DELETE /api/projects/{project_id}/files/{file_id}
 */
export async function deleteProjectFile(projectId: string, fileId: string): Promise<void> {
  await api.delete(`/api/projects/${projectId}/files/${fileId}`)
}

